"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
const jsx_runtime_1 = require("react/jsx-runtime");
/**
 * CohortPlanEditPage - Edit cohort master degree plan with mass update cascade
 *
 * When updated, cascades term/course changes to all non-off-sequence student plans.
 */
const react_1 = require("react");
const react_router_dom_1 = require("react-router-dom");
const validation_1 = require("../../helpers/validation");
const ToastProvider_1 = require("../../providers/ToastProvider");
const TermPlanningGrid_1 = require("../../components/TermPlanningGrid");
const Button_1 = require("../../components/Button");
const Modal_1 = require("../../components/Modal");
const LoadingSpinner_1 = require("../../components/LoadingSpinner");
const degreePlanApi_1 = require("../../helpers/degreePlanApi");
const termApi_1 = require("../../helpers/termApi");
const courseApi_1 = require("../../helpers/courseApi");
const academicProgramApi_1 = require("../../helpers/academicProgramApi");
require("./CohortPlanEditPage.scss");
const CohortPlanEditPage = () => {
    var _a;
    const { cohortId, organizationId } = (0, react_router_dom_1.useParams)();
    const navigate = (0, react_router_dom_1.useNavigate)();
    const { success, error: showError } = (0, ToastProvider_1.useToast)();
    // State
    const [degreePlan, setDegreePlan] = (0, react_1.useState)(null);
    const [availableTerms, setAvailableTerms] = (0, react_1.useState)([]);
    const [availableCourses, setAvailableCourses] = (0, react_1.useState)([]);
    const [termPlans, setTermPlans] = (0, react_1.useState)([]);
    // Snapshot for dirty tracking
    const [snapshot, setSnapshot] = (0, react_1.useState)('');
    // Loading states
    const [loading, setLoading] = (0, react_1.useState)(true);
    const [saving, setSaving] = (0, react_1.useState)(false);
    const [showUpdateConfirm, setShowUpdateConfirm] = (0, react_1.useState)(false);
    const [massUpdateProgress, setMassUpdateProgress] = (0, react_1.useState)(null);
    const isDirty = snapshot !== '' && JSON.stringify(termPlans) !== snapshot;
    // Validate URL parameters
    (0, react_1.useEffect)(() => {
        if (cohortId && organizationId) {
            try {
                (0, validation_1.validateId)(cohortId, 'Cohort ID');
                (0, validation_1.validateId)(organizationId, 'Organization ID');
            }
            catch (error) {
                console.error('Invalid route parameters:', error);
            }
        }
    }, [cohortId, organizationId]);
    // Load data
    (0, react_1.useEffect)(() => {
        if (!cohortId || !organizationId)
            return;
        let cancelled = false;
        const loadData = () => __awaiter(void 0, void 0, void 0, function* () {
            var _a;
            setLoading(true);
            try {
                // Load cohort degree plan and reference data in parallel
                const plan = yield degreePlanApi_1.degreePlanApi.getDegreePlanByCohortId(cohortId);
                if (cancelled)
                    return;
                const [terms, courses] = yield Promise.all([
                    termApi_1.termApi.getTermsByOrganization(organizationId),
                    courseApi_1.courseApi.getCoursesByOrganization(organizationId),
                ]);
                if (cancelled)
                    return;
                // Resolve stubs BEFORE creating TermPlanItems
                const termsMap = new Map(terms.map(t => [t.id, t]));
                const coursesMap = new Map(courses.map(c => [c.id, c]));
                // Resolve academicProgram name if it's a stub
                let resolvedPlan = plan;
                if (((_a = plan.academicProgram) === null || _a === void 0 ? void 0 : _a.id) && !plan.academicProgram.name) {
                    try {
                        const program = yield academicProgramApi_1.academicProgramApi.getProgramById(plan.academicProgram.id);
                        resolvedPlan = Object.assign(Object.assign({}, plan), { academicProgram: program });
                    }
                    catch (_b) {
                        // Keep stub if fetch fails
                    }
                }
                setDegreePlan(resolvedPlan);
                const planItems = resolvedPlan.terms.map((term) => ({
                    id: term.id,
                    term: termsMap.get(term.term.id) || term.term,
                    position: term.position,
                    courses: term.courses.map((coursePlan) => ({
                        id: `${term.id}-${coursePlan.course.id}`,
                        course: coursesMap.get(coursePlan.course.id) || coursePlan.course,
                        failed: coursePlan.failed,
                    })),
                }));
                setTermPlans(planItems);
                setSnapshot(JSON.stringify(planItems));
                setAvailableTerms(terms);
                setAvailableCourses(courses.filter(c => c.approved));
            }
            catch (err) {
                if (!cancelled) {
                    showError(err instanceof Error ? err.message : 'Failed to load cohort degree plan');
                }
            }
            finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        });
        loadData();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cohortId, organizationId]);
    const handleMassUpdate = (0, react_1.useCallback)(() => __awaiter(void 0, void 0, void 0, function* () {
        if (!degreePlan)
            return;
        setSaving(true);
        setShowUpdateConfirm(false);
        try {
            // 1. Build updated cohort plan (spread full plan so cohort/org/tenantId are included)
            const updatedTerms = termPlans.map((tp) => ({
                id: tp.id,
                term: tp.term,
                position: tp.position,
                courses: tp.courses.map((cp) => ({
                    course: cp.course,
                    failed: cp.failed || false,
                })),
            }));
            const updatedPlan = Object.assign(Object.assign({}, degreePlan), { terms: updatedTerms });
            // 2. Update cohort master plan
            yield degreePlanApi_1.degreePlanApi.updateCohortPlan(updatedPlan);
            success('The cohort plan has been updated!');
            // 3. Fetch student plans for this cohort
            const studentPlans = yield degreePlanApi_1.degreePlanApi.getStudentPlansByCohortId(degreePlan.cohort.id);
            // 4. Filter to non-off-sequence students and cascade
            const eligiblePlans = studentPlans.filter((sp) => { var _a, _b; return ((_a = sp.profile) === null || _a === void 0 ? void 0 : _a.id) && !sp.offSequence && ((_b = sp.cohort) === null || _b === void 0 ? void 0 : _b.id) === degreePlan.cohort.id; });
            const skippedCount = studentPlans.length - eligiblePlans.length;
            const progress = {
                total: studentPlans.length,
                updated: 0,
                skipped: skippedCount,
                errors: 0,
            };
            setMassUpdateProgress(Object.assign({}, progress));
            // 5. Update each eligible student plan with the new terms
            for (const studentPlan of eligiblePlans) {
                try {
                    const updatedStudentPlan = Object.assign(Object.assign({}, studentPlan), { terms: updatedTerms });
                    yield degreePlanApi_1.degreePlanApi.updateStudentPlan(updatedStudentPlan);
                    progress.updated++;
                }
                catch (_a) {
                    progress.errors++;
                }
                setMassUpdateProgress(Object.assign({}, progress));
            }
            // 6. Show final summary and reset dirty state (stay on page)
            if (progress.errors === 0) {
                success('The student plans have been updated!');
            }
            else {
                showError(`Updated ${progress.updated} student plans. ${progress.errors} failed. ${progress.skipped} off-sequence skipped.`);
            }
            setSnapshot(JSON.stringify(termPlans));
            setMassUpdateProgress(null);
        }
        catch (err) {
            showError(err instanceof Error ? err.message : 'Failed to update cohort plan');
            setMassUpdateProgress(null);
        }
        finally {
            setSaving(false);
        }
    }), [degreePlan, termPlans, success, showError]);
    const handleBackToList = (0, react_1.useCallback)(() => {
        var _a;
        navigate('/degree-planning', {
            state: degreePlan
                ? { orgId: (_a = degreePlan.organization) === null || _a === void 0 ? void 0 : _a.id, org: degreePlan.organization }
                : undefined,
        });
    }, [navigate, degreePlan]);
    // Redirect if required parameters are missing
    if (!cohortId || !organizationId) {
        return (0, jsx_runtime_1.jsx)(react_router_dom_1.Navigate, { to: "/degree-planning", replace: true });
    }
    if (loading) {
        return ((0, jsx_runtime_1.jsx)("div", { className: "cohort-plan-edit-page", children: (0, jsx_runtime_1.jsx)(LoadingSpinner_1.LoadingSpinner, { label: "Loading cohort degree plan..." }) }));
    }
    if (!degreePlan) {
        return ((0, jsx_runtime_1.jsxs)("div", { className: "cohort-plan-edit-page", children: [(0, jsx_runtime_1.jsx)("h1", { children: "Cohort Plan Not Found" }), (0, jsx_runtime_1.jsx)("p", { children: "The requested cohort degree plan could not be found." }), (0, jsx_runtime_1.jsx)(Button_1.Button, { onClick: () => navigate('/degree-planning'), children: "Back to Plans" })] }));
    }
    return ((0, jsx_runtime_1.jsxs)("div", { className: "cohort-plan-edit-page", children: [(0, jsx_runtime_1.jsx)("div", { className: "cohort-plan-edit-page__back-button", children: (0, jsx_runtime_1.jsx)(Button_1.Button, { variant: "ghost", onClick: handleBackToList, children: "\u2190 Back to List" }) }), (0, jsx_runtime_1.jsxs)("div", { className: "cohort-plan-edit-page__header", children: [(0, jsx_runtime_1.jsx)("h1", { className: "cohort-plan-edit-page__title", children: "Edit Cohort Degree Plan" }), (0, jsx_runtime_1.jsxs)("p", { className: "cohort-plan-edit-page__subtitle", children: [degreePlan.cohort.name, " - ", ((_a = degreePlan.academicProgram) === null || _a === void 0 ? void 0 : _a.name) || 'Unknown Program'] })] }), (0, jsx_runtime_1.jsx)("div", { className: "cohort-plan-edit-page__content", children: (0, jsx_runtime_1.jsx)("section", { className: "cohort-plan-edit-page__section", children: (0, jsx_runtime_1.jsx)(TermPlanningGrid_1.TermPlanningGrid, { termPlans: termPlans, availableTerms: availableTerms, availableCourses: availableCourses, onChange: setTermPlans, searchableCourseSelect: true }) }) }), massUpdateProgress && ((0, jsx_runtime_1.jsx)("div", { className: "cohort-plan-edit-page__progress", role: "status", children: (0, jsx_runtime_1.jsxs)("p", { children: ["Updating student plans: ", massUpdateProgress.updated, " of", ' ', massUpdateProgress.total - massUpdateProgress.skipped, " eligible", massUpdateProgress.skipped > 0 && ((0, jsx_runtime_1.jsxs)("span", { children: [" (", massUpdateProgress.skipped, " off-sequence skipped)"] })), massUpdateProgress.errors > 0 && ((0, jsx_runtime_1.jsxs)("span", { children: [" (", massUpdateProgress.errors, " errors)"] }))] }) })), (0, jsx_runtime_1.jsxs)("div", { className: "cohort-plan-edit-page__actions", children: [(0, jsx_runtime_1.jsx)(Button_1.Button, { variant: "secondary", onClick: () => navigate('/degree-planning'), children: "Cancel" }), (0, jsx_runtime_1.jsx)("span", { title: !isDirty && !saving ? 'No changes to save — edit the plan to enable Update' : undefined, style: { display: 'inline-flex' }, children: (0, jsx_runtime_1.jsx)(Button_1.Button, { onClick: () => setShowUpdateConfirm(true), loading: saving, disabled: saving || !isDirty, children: "Update" }) })] }), (0, jsx_runtime_1.jsx)(Modal_1.Modal, { isOpen: showUpdateConfirm, onClose: () => setShowUpdateConfirm(false), title: "Mass Updating Cohort Degree Plans", footer: (0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsx)(Button_1.Button, { variant: "secondary", onClick: () => setShowUpdateConfirm(false), children: "Cancel" }), (0, jsx_runtime_1.jsx)(Button_1.Button, { onClick: handleMassUpdate, loading: saving, children: "Confirm Update" })] }), children: (0, jsx_runtime_1.jsx)("p", { children: "Are you sure you want to mass update all degree plans for this cohort? If so, remember that off-sequence plans cannot be updated." }) })] }));
};
exports.default = CohortPlanEditPage;
